import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { isNumber, noop } from 'lodash';
import { Subject } from 'rxjs';
import { GeolocationDataBuilder } from '../../../geolocation-data/geolocation-data.builder';
import { GeolocationDataService } from '../../../geolocation-data/geolocation-data.service';
import { ImageBuilder } from '../../../shared/builders/image/image.builder';
import {
  ITrackingAction,
  ITrackingObject,
} from '../../../tracking/tracking.data';
import { filter, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-map-card',
  templateUrl: './map-card.component.html',
  styleUrls: ['./map-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() latitude: number;
  @Input() longitude: number;
  @Input() zoomLevel: number = 15;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;

  @Output() onAddressNotFound: EventEmitter<void> = new EventEmitter<void>();

  private _fetchImage: Subject<void> = new Subject<void>();
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  mapImageUrl: SafeResourceUrl;
  externalMapsUrl: string;
  fetching: boolean;
  height = 250; // in pixels
  width = 550; // in pixels

  constructor(
    private _geolocationDataService: GeolocationDataService,
    private _imageBuilder: ImageBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private _geolocationDataBuilder: GeolocationDataBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this._fetchImage.next();

    if (
      (changes.latitude || changes.longitude) &&
      isNumber(this.latitude) &&
      isNumber(this.longitude)
    ) {
      this.externalMapsUrl = this._geolocationDataBuilder.buildGoogleMapsUrl(
        this.latitude,
        this.longitude
      );
    }
  }

  ngOnInit() {
    this._fetchImage
      .asObservable()
      .pipe(
        takeUntil(this._destroyed),
        startWith(void 0),
        filter(() => {
          return (
            isNumber(this.latitude) &&
            isNumber(this.longitude) &&
            isNumber(this.zoomLevel)
          );
        }),
        tap(() => {
          this.fetching = true;

          this._changeDetectorRef.markForCheck();
        }),
        switchMap(() =>
          this._geolocationDataService.getMapImageUrl(
            this.latitude,
            this.longitude,
            this.zoomLevel,
            this.height,
            this.width
          )
        )
      )
      .subscribe((imageBlob: Blob) => {
        this.mapImageUrl = this._imageBuilder.buildSafeImageUrl(imageBlob);
        this.fetching = false;

        this._changeDetectorRef.markForCheck();
      }, noop);
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();

    this._fetchImage.unsubscribe();
  }
}
